import { useCallback, useEffect, useState } from "react";
import { currentHeadingIndex } from "./current-heading";

const SCROLL_KEYS = new Set(["PageUp", "PageDown", "ArrowUp", "ArrowDown", "Home", "End", " "]);

/**
 * scroller のスクロールに合わせて、今いる節の見出しの番号と、要素へスクロールする関数を返す。
 * 末尾の短い節は上端まで届かないので、スクロールで移った見出しはユーザーが自分でスクロールするまで今いる節として扱う。
 * pageKey が変わったら、その固定を外す。
 */
export function useCurrentHeading(
	scroller: HTMLElement | null,
	headings: readonly HTMLElement[],
	pageKey: string,
): [number, (target: Element) => void] {
	const [index, setIndex] = useState(-1);
	const [pinned, setPinned] = useState<Element | null>(null);

	const [prevPageKey, setPrevPageKey] = useState(pageKey);
	if (prevPageKey !== pageKey) {
		setPrevPageKey(pageKey);
		setPinned(null);
	}

	useEffect(() => {
		if (!scroller) return;
		const update = () => {
			const scrollerTop = scroller.getBoundingClientRect().top;
			const tops = headings.map((h) => h.getBoundingClientRect().top - scrollerTop);
			const scrolledToBottom =
				scroller.scrollTop > 0 &&
				scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight <= 1;
			setIndex(currentHeadingIndex(tops, scrolledToBottom));
		};
		update();
		scroller.addEventListener("scroll", update, { passive: true });
		window.addEventListener("resize", update);
		return () => {
			scroller.removeEventListener("scroll", update);
			window.removeEventListener("resize", update);
		};
	}, [scroller, headings]);

	useEffect(() => {
		if (!pinned) return;
		const unpin = () => setPinned(null);
		const unpinOnScrollKey = (e: KeyboardEvent) => {
			if (SCROLL_KEYS.has(e.key)) unpin();
		};
		// スクロールバーを押したときだけ。本文のクリックや文字の選択では外さない
		const unpinOnScrollbar = (e: PointerEvent) => {
			if (scroller && e.target === scroller) unpin();
		};
		window.addEventListener("wheel", unpin, { passive: true });
		window.addEventListener("touchstart", unpin, { passive: true });
		window.addEventListener("keydown", unpinOnScrollKey);
		window.addEventListener("pointerdown", unpinOnScrollbar);
		return () => {
			window.removeEventListener("wheel", unpin);
			window.removeEventListener("touchstart", unpin);
			window.removeEventListener("keydown", unpinOnScrollKey);
			window.removeEventListener("pointerdown", unpinOnScrollbar);
		};
	}, [pinned, scroller]);

	const scrollTo = useCallback((target: Element) => {
		setPinned(target);
		target.scrollIntoView({ behavior: "smooth", block: "start" });
	}, []);

	const pinnedIndex = pinned ? headings.indexOf(pinned as HTMLElement) : -1;
	return [pinnedIndex >= 0 ? pinnedIndex : index, scrollTo];
}
